import React, { useState, useEffect, useRef } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { AlertCircle, X as XIcon } from 'lucide-react';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { roleSettingsPath } from '../../../../services/api/chartRolesApi';
import { bookingSuggestionTitle } from '../../../../services/BookingProposalService';
import { formatCurrency } from '../bankFormat';
import ActionPreview from '../reconciliation/ActionPreview';
import EvidencePicker, { evidenceName } from '../reconciliation/EvidencePicker';
import './booking-editor.css';

export const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 30,
        height: 30,
        fontSize: '0.8125rem',
        borderRadius: 8,
        borderColor: state.isFocused ? '#44814E' : '#aeb6c2',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(68, 129, 78, 0.10)' : 'none',
        '&:hover': { borderColor: state.isFocused ? '#44814E' : '#8793a1' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: 28 }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    menu: (base) => ({ ...base, fontSize: '0.8125rem', zIndex: 5 }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    clearIndicator: (base) => ({ ...base, padding: 4 }),
};

const rowSelectValue = (row) => (row.account_number
    ? { value: String(row.account_number), label: row.account_name ? `${row.account_number} - ${row.account_name}` : String(row.account_number) }
    : null);

/**
 * The "book this transaction" form: description, fiscal year when it could
 * not be inferred, the kontering rows, and — when the reconciliation
 * purchase path applies — the receipt and VAT treatment. Templates fold
 * behind a text action. No buttons of its own: the host's footer decides.
 *
 * `draft` is useBookingDraft's result; `purchase` is usePurchaseAction's
 * result or null. While a purchase preview exists, the editor shows the
 * immutable preview instead of the form.
 */
export default function BookingEditor({ companyId, draft, purchase = null, templatesFirst = false }) {
    const [view, setView] = useState('rows'); // 'rows' | 'templates'
    // `templatesFirst`: there was nothing to fill the rows from, so the view
    // opens on the bokföringsmallar (the supplier invoice page starts its
    // payment step the same way). Once, when the templates have arrived.
    const openedOnTemplates = useRef(false);
    useEffect(() => {
        if (!templatesFirst || openedOnTemplates.current) return;
        if (draft.templatesLoading || draft.templates.length === 0) return;
        openedOnTemplates.current = true;
        setView('templates');
    }, [templatesFirst, draft.templatesLoading, draft.templates.length]);
    const [templateSearch, setTemplateSearch] = useState('');

    if (purchase?.action?.plan) {
        return (
            <div className="booking-editor">
                <ActionPreview plan={purchase.action.plan} flat />
                <p className="booking-editor__hint" style={{ marginTop: 10 }}>
                    Detta bokförs när du bekräftar. Gå tillbaka för att ändra kontering eller underlag.
                </p>
            </div>
        );
    }

    const balanceOk = draft.isBalanced && draft.rowsAreComplete;
    const diff = Math.round((draft.totalDebit - draft.totalCredit) * 100) / 100;

    const visibleTemplates = draft.templates.filter((template) => {
        const query = templateSearch.trim().toLowerCase();
        if (!query) return true;
        return `${template.template_name || ''} ${template.description || ''}`.toLowerCase().includes(query);
    });

    const applyTemplate = async () => {
        if (await draft.applyTemplate()) setView('rows');
    };

    return (
        <div className="booking-editor">
            {draft.fiscalYearsError && (
                <Alert variant="warning" className="py-2 d-flex align-items-center justify-content-between" style={{ fontSize: '0.8125rem' }}>
                    <span><AlertCircle size={14} className="me-2" />{draft.fiscalYearsError}</span>
                    <Button size="sm" variant="outline-secondary" onClick={draft.fetchFiscalYears}>Försök igen</Button>
                </Alert>
            )}
            {draft.roleLegMissing && (
                <Alert variant="warning" className="py-2 d-flex align-items-center justify-content-between" style={{ fontSize: '0.8125rem' }}>
                    <span>
                        <AlertCircle size={14} className="me-2" />
                        {draft.neededRole === 'tax_account'
                            ? 'Rollen Skattekonto är inte mappad i din kontoplan — betalkontoraden kan inte förifyllas.'
                            : 'Rollen Företagskonto är inte mappad i din kontoplan — betalkontoraden kan inte förifyllas.'}
                    </span>
                    <Button size="sm" variant="outline-secondary" href={roleSettingsPath(companyId)}>Mappa rollen</Button>
                </Alert>
            )}
            {(draft.error || purchase?.error) && (
                <Alert variant="danger" className="py-2" style={{ fontSize: '0.8125rem' }} dismissible onClose={() => draft.setError(null)}>
                    <AlertCircle size={14} className="me-2" />{draft.error || purchase?.error}
                </Alert>
            )}
            {purchase?.unavailable && (
                <p className="booking-editor__hint" style={{ marginBottom: 12 }}>
                    Kunde inte kontrollera automatisk avstämning. Verifikatet bokförs utan koppling till underlag.
                </p>
            )}

            {view === 'templates' ? (
                <div>
                    <div className="d-flex justify-content-between align-items-baseline" style={{ marginBottom: 8 }}>
                        <span className="booking-editor__label" style={{ marginBottom: 0 }}>Bokföringsmall</span>
                        <button type="button" className="booking-editor__action" onClick={() => { setView('rows'); draft.setSelectedTemplate(null); }}>
                            Tillbaka till konteringen
                        </button>
                    </div>
                    {!draft.selectedTemplate && (
                        <>
                            <Form.Control
                                type="search"
                                value={templateSearch}
                                onChange={(event) => setTemplateSearch(event.target.value)}
                                placeholder="Sök bland alla mallar…"
                                className="mb-2"
                            />
                            {draft.templatesLoading && <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>}
                            {draft.templateError && <Alert variant="danger" className="py-2" style={{ fontSize: '0.8125rem' }}>{draft.templateError}</Alert>}
                            {!draft.templatesLoading && visibleTemplates.map((template) => (
                                <button
                                    type="button"
                                    key={template.template_id}
                                    className="booking-editor__list-item"
                                    disabled={draft.templateEvaluating}
                                    onClick={() => draft.selectTemplate(template)}
                                >
                                    <strong>{template.template_name}</strong>
                                    {template.description && <small>{template.description}</small>}
                                </button>
                            ))}
                            {!draft.templatesLoading && visibleTemplates.length === 0 && (
                                <p className="booking-editor__hint">Inga mallar matchar sökningen.</p>
                            )}
                        </>
                    )}
                    {draft.selectedTemplate && (
                        <div>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                    <strong style={{ fontSize: '0.875rem' }}>{draft.selectedTemplate.template_name}</strong>
                                    {draft.selectedTemplate.description && (
                                        <div className="booking-editor__hint">{draft.selectedTemplate.description}</div>
                                    )}
                                </div>
                                <button type="button" className="booking-editor__action" onClick={() => draft.setSelectedTemplate(null)}>Byt mall</button>
                            </div>
                            {draft.templateError && <Alert variant="danger" className="py-2" style={{ fontSize: '0.8125rem' }}>{draft.templateError}</Alert>}
                            {(draft.selectedTemplate.body?.inputs || []).map((input) => (
                                <Form.Group className="booking-editor__field" key={input.name}>
                                    <Form.Label>{input.label || input.name}{input.required ? ' *' : ''}</Form.Label>
                                    {input.type === 'account_picker' ? (
                                        <Select
                                            options={draft.accounts}
                                            theme={brandSelectTheme}
                                            styles={selectStyles}
                                            value={draft.accounts.find((account) => account.value === String(draft.templateInputs[input.name])) || null}
                                            onChange={(option) => draft.setTemplateInputs((current) => ({ ...current, [input.name]: option?.value || '' }))}
                                            placeholder="Välj konto…"
                                            isClearable
                                        />
                                    ) : input.type === 'select' ? (
                                        <Form.Select
                                            value={draft.templateInputs[input.name] ?? ''}
                                            onChange={(event) => draft.setTemplateInputs((current) => ({ ...current, [input.name]: event.target.value }))}
                                        >
                                            <option value="">Välj…</option>
                                            {(input.options || []).map((option) => {
                                                const value = typeof option === 'object' ? option.value : option;
                                                const label = typeof option === 'object' ? option.label : option;
                                                return <option key={value} value={value}>{label}</option>;
                                            })}
                                        </Form.Select>
                                    ) : input.type === 'boolean' ? (
                                        <Form.Check
                                            type="checkbox"
                                            checked={Boolean(draft.templateInputs[input.name])}
                                            onChange={(event) => draft.setTemplateInputs((current) => ({ ...current, [input.name]: event.target.checked }))}
                                        />
                                    ) : (
                                        <Form.Control
                                            type={input.type === 'date' ? 'date' : ['currency', 'currency_gross', 'currency_net', 'number', 'integer'].includes(input.type) ? 'number' : 'text'}
                                            step={input.type === 'integer' ? '1' : '0.01'}
                                            value={draft.templateInputs[input.name] ?? ''}
                                            onChange={(event) => draft.setTemplateInputs((current) => ({ ...current, [input.name]: event.target.value }))}
                                        />
                                    )}
                                    {input.help_text && <Form.Text className="booking-editor__hint">{input.help_text}</Form.Text>}
                                </Form.Group>
                            ))}
                            <Button size="sm" onClick={applyTemplate} disabled={draft.templateEvaluating}>
                                {draft.templateEvaluating ? <Spinner animation="border" size="sm" /> : 'Använd mall'}
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <Form onSubmit={(e) => e.preventDefault()}>
                    <Form.Group className="booking-editor__field">
                        <Form.Label htmlFor="booking-description">Beskrivning</Form.Label>
                        <Form.Control
                            id="booking-description"
                            type="text"
                            value={draft.description}
                            onChange={(e) => draft.setDescription(e.target.value)}
                        />
                    </Form.Group>

                    {/* The fiscal year is inferred from the booking date; the
                        select only appears when that failed. */}
                    {!draft.fiscalYearsError && draft.fiscalYears.length === 0 && !draft.selectedFiscalYear && (
                        <p className="booking-editor__hint" style={{ marginBottom: 14 }}>Inget räkenskapsår är upplagt ännu.</p>
                    )}
                    {draft.fiscalYears.length > 0 && !draft.selectedFiscalYear && (
                        <Form.Group className="booking-editor__field">
                            <Form.Label htmlFor="booking-fiscal-year">Räkenskapsår</Form.Label>
                            <Form.Select
                                id="booking-fiscal-year"
                                value={draft.selectedFiscalYear || ''}
                                onChange={(e) => draft.setSelectedFiscalYear(e.target.value || null)}
                            >
                                <option value="">Välj räkenskapsår…</option>
                                {draft.fiscalYears.map((fy) => (
                                    <option key={fy.fiscal_year_id} value={fy.fiscal_year_id}>
                                        {fy.name} ({fy.start_date} – {fy.end_date})
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="booking-editor__hint">Inget räkenskapsår täcker bokföringsdatumet.</Form.Text>
                        </Form.Group>
                    )}

                    <div className="booking-editor__section">
                        <div className="d-flex justify-content-between align-items-baseline">
                            <span className="booking-editor__label">Kontering</span>
                            {draft.proposal && (
                                <span className="booking-editor__hint">Från förslag: {bookingSuggestionTitle(draft.proposal)}</span>
                            )}
                        </div>
                        <div className="booking-editor__row-heading" aria-hidden="true">
                            <span>Konto</span><span>Debet</span><span>Kredit</span><span />
                        </div>
                        {draft.rows.map((row, index) => (
                            <div className="booking-editor__row" key={index}>
                                <Select
                                    aria-label={`Konto rad ${index + 1}`}
                                    options={draft.accounts}
                                    theme={brandSelectTheme}
                                    styles={selectStyles}
                                    isSearchable
                                    placeholder="Välj konto…"
                                    noOptionsMessage={() => 'Inga konton hittades'}
                                    value={rowSelectValue(row)}
                                    onChange={(option) => draft.selectRowAccount(index, option)}
                                />
                                <Form.Control
                                    aria-label={`Debet rad ${index + 1}`}
                                    type="number"
                                    inputMode="decimal"
                                    step="0.01"
                                    min="0"
                                    placeholder="0,00"
                                    value={row.debit}
                                    onChange={(e) => draft.updateRow(index, 'debit', e.target.value)}
                                />
                                <Form.Control
                                    aria-label={`Kredit rad ${index + 1}`}
                                    type="number"
                                    inputMode="decimal"
                                    step="0.01"
                                    min="0"
                                    placeholder="0,00"
                                    value={row.credit}
                                    onChange={(e) => draft.updateRow(index, 'credit', e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="booking-editor__remove"
                                    aria-label="Ta bort rad"
                                    disabled={draft.rows.length <= 2}
                                    onClick={() => draft.removeRow(index)}
                                >
                                    <XIcon size={14} />
                                </button>
                            </div>
                        ))}
                        <div className={`booking-editor__balance ${balanceOk ? 'booking-editor__balance--ok' : 'booking-editor__balance--off'}`}>
                            <button type="button" className="booking-editor__action" onClick={draft.addRow}>Lägg till rad</button>
                            <span>
                                <strong>{balanceOk ? 'Balanserad' : draft.rowsAreComplete ? `Diff ${formatCurrency(Math.abs(diff))}` : 'Ofullständig'}</strong>
                                {' · '}D {formatCurrency(draft.totalDebit)} · K {formatCurrency(draft.totalCredit)}
                            </span>
                        </div>
                    </div>

                    <div className="booking-editor__section d-flex flex-column align-items-start" style={{ gap: 8 }}>
                        {draft.templates.length > 0 && (
                            <button type="button" className="booking-editor__action" onClick={() => setView('templates')}>
                                Välj bokföringsmall
                            </button>
                        )}
                        {purchase?.canAttachReceipt && (
                            purchase.doc ? (
                                <span style={{ fontSize: '0.8125rem' }}>
                                    Underlag: {evidenceName(purchase.doc)}
                                    {' '}
                                    <button type="button" className="booking-editor__action" onClick={purchase.clearDoc}>Ta bort</button>
                                </span>
                            ) : (
                                <button type="button" className="booking-editor__action" onClick={() => purchase.setPickerOpen(true)}>
                                    Lägg till underlag
                                </button>
                            )
                        )}
                    </div>

                    {purchase?.canAttachReceipt && (
                        <EvidencePicker companyId={companyId} show={purchase.pickerOpen} onHide={() => purchase.setPickerOpen(false)} onPick={purchase.setDoc} />
                    )}
                </Form>
            )}
        </div>
    );
}
